import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import {
  type ConnectedClient,
  type CurrentUser,
  api,
  formatBytes,
  formatDate,
} from "@/lib/api"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  RefreshCw,
  Wifi,
  ArrowDownToLine,
  ArrowUpFromLine,
  Users,
} from "lucide-react"

type Props = { selectedMachine: string; currentUser: CurrentUser }

export function DashboardPage({ selectedMachine, currentUser }: Props) {
  const [clients, setClients] = useState<ConnectedClient[]>([])
  const [loading, setLoading] = useState(() =>
    Boolean(selectedMachine && currentUser.permissions.includes("view"))
  )
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [log, setLog] = useState("")
  const logRef = useRef<HTMLPreElement>(null)

  const hasPermission = currentUser.permissions.includes("view")

  const refresh = useCallback(async () => {
    if (!selectedMachine || !hasPermission) return
    setLoading(true)
    setClients([])
    setLog("")
    setLastUpdated(null)
    try {
      const res = await api.getConnectedClients(selectedMachine)
      setClients(res.clients)
      setLog(res.log)
      setLastUpdated(new Date())
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao buscar dados.")
    } finally {
      setLoading(false)
    }
  }, [selectedMachine, hasPermission])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [log])

  const totalRx = clients.reduce((s, c) => s + c.bytes_received, 0)
  const totalTx = clients.reduce((s, c) => s + c.bytes_sent, 0)

  if (!hasPermission) {
    return (
      <div className="flex w-full flex-1 flex-col items-start justify-start p-4 sm:p-6 md:p-8">
        <p className="text-sm text-muted-foreground">
          Sem permissão para acessar o dashboard.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-full min-w-0 flex-1 flex-col gap-6 p-4 sm:p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {selectedMachine
              ? `Ambiente: ${selectedMachine}`
              : "Nenhum ambiente selecionado"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading || !selectedMachine}
        >
          <RefreshCw className={loading ? "animate-spin" : ""} />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Conectados agora"
          value={loading ? null : clients.length}
          icon={<Users className="size-4 text-primary" />}
          accent="primary"
        />
        <StatCard
          label="Volume recebido"
          value={loading ? null : formatBytes(totalRx)}
          icon={<ArrowDownToLine className="size-4 text-emerald-500" />}
          accent="green"
        />
        <StatCard
          label="Volume enviado"
          value={loading ? null : formatBytes(totalTx)}
          icon={<ArrowUpFromLine className="size-4 text-violet-400" />}
          accent="purple"
        />
      </div>

      <div className="flex min-w-0 flex-col gap-4">
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-medium">
                  Usuários conectados
                </CardTitle>
                <CardDescription className="text-xs">
                  {lastUpdated
                    ? `Atualizado às ${lastUpdated.toLocaleTimeString("pt-BR")}`
                    : "Ainda não atualizado"}
                </CardDescription>
              </div>
              {clients.length > 0 && (
                <Badge variant="secondary" className="font-mono">
                  {clients.length} online
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="space-y-2 px-6 pb-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : clients.length === 0 ? (
              <div className="flex min-h-48 flex-col items-start gap-3 px-6 py-12 sm:min-h-56">
                <Wifi className="size-8 text-muted-foreground/40" />
                <p className="w-full text-left text-sm text-muted-foreground">
                  Nenhum usuário conectado no momento.
                </p>
              </div>
            ) : (
              <ScrollArea>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                        Nome
                      </th>
                      <th className="px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                        Endereço real
                      </th>
                      <th className="px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                        IP VPN
                      </th>
                      <th className="px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                        Conectado desde
                      </th>
                      <th className="px-4 py-2 text-right font-mono text-[11px] font-medium text-muted-foreground">
                        Transferência
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr
                        key={c.name}
                        className="border-b transition-colors last:border-0 hover:bg-muted/30"
                      >
                        <td className="px-4 py-2.5 font-medium">
                          <div className="flex items-center gap-2">
                            <span className="pulse-dot size-1.5 shrink-0 rounded-full bg-emerald-500" />
                            {c.name}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                          {c.real_address}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-primary">
                          {c.vpn_ip}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">
                          {formatDate(c.connected_since)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground">
                          ↓{formatBytes(c.bytes_received)} ↑
                          {formatBytes(c.bytes_sent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Saída recente</CardTitle>
            <CardDescription className="text-xs">
              Log da última operação
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-60">
              <pre
                ref={logRef}
                className="px-4 pb-4 font-mono text-[11px] leading-relaxed break-all whitespace-pre-wrap text-muted-foreground"
              >
                {log || "Aguardando operações..."}
              </pre>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string
  value: string | number | null
  icon: React.ReactNode
  accent: "primary" | "green" | "purple"
}) {
  const border = {
    primary: "border-primary/20",
    green: "border-emerald-500/20",
    purple: "border-violet-400/20",
  }[accent]

  return (
    <Card className={`border ${border}`}>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            {value === null ? (
              <Skeleton className="mt-1.5 h-7 w-20" />
            ) : (
              <p className="mt-0.5 font-mono text-2xl font-bold">{value}</p>
            )}
          </div>
          <div className="mt-0.5 rounded-lg border bg-muted/40 p-2">{icon}</div>
        </div>
      </CardContent>
    </Card>
  )
}
